/**
 * №13.1 (§1.5 / №11.1.2) — client mirror of the declared governed-field rules.
 *
 * The engine is the enforcer: a generic `update_<slug>` touching a locked field
 * on a governed-status row is refused with a `field_governed` teaching error.
 * This map lets the chrome render that teaching UI **proactively** — a padlock +
 * the correction sentence, never an editable input that fails on save (§1.5).
 * It intentionally duplicates the module `governance` declarations
 * (src/modules/{legal,commercial-spine}/definition.ts) because the client
 * `EntityTypeDefinition` does not carry the governance block; keep the two in
 * sync. `isFieldGovernedError` is the reactive fallback for anything not mirrored
 * here (the engine sentence is shown verbatim).
 */

export interface GovernanceRule {
  /** Status values under which the row is frozen. */
  whenStatus: string[];
  /** Fields that stay editable even while governed (e.g. invoice.ar_status). */
  except?: string[];
  /** The teaching sentence naming the correction path. */
  correction: string;
}

export const GOVERNANCE: Record<string, GovernanceRule> = {
  matter: {
    whenStatus: ["closed"],
    correction: "This matter is closed — reopen it (Reopen matter) before editing its details.",
  },
  "fee-arrangement": {
    whenStatus: ["closed"],
    correction: "This fee arrangement is closed — reopen it before editing.",
  },
  "budget-version": {
    whenStatus: ["approved", "superseded"],
    correction: "Approved budgets are immutable — create a new draft budget version to revise the figures.",
  },
  "time-entry": {
    whenStatus: ["invoiced"],
    correction: "This time entry is billed onto a posted invoice — adjust it with a credit note, not a generic edit.",
  },
  invoice: {
    whenStatus: ["posted"],
    except: ["ar_status"],
    correction: "A posted invoice is immutable — issue a typed credit note, never a generic edit.",
  },
};

/** The governance rule in force for a row, if its status freezes it. */
export function governanceFor(slug: string | undefined, status: unknown): GovernanceRule | null {
  if (!slug) return null;
  const rule = GOVERNANCE[slug];
  if (!rule) return null;
  return typeof status === "string" && rule.whenStatus.includes(status) ? rule : null;
}

/** Reactive fallback: detect the engine's `field_governed` teaching error. */
export function fieldGovernedMessage(err: unknown): string | null {
  const msg = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  if (!msg) return null;
  return /field_governed|immutable|is closed|is billed|credit note|new draft/i.test(msg) ? msg : null;
}
